import React, { useState, useContext } from 'react'
import EditIcon from '@material-ui/icons/Edit'
import Chip from '@material-ui/core/Chip'
import Box from '@material-ui/core/Box'

import { Dialog, snackbar } from 'components'
import { useApi, useApiCall } from 'hooks'
import { ERRORS, STRINGS } from 'consts'
import { UserContext } from 'contexts/UserContext'
import { hasRole, Role as RoleEnum } from 'utils/roles'

import { deleteAdmin, getAdmins, getRoles } from './api'
import { NewAdminDialog, UpdateAdminDialog } from './components'
import { DefaultTable } from '../components'
import { Admin, GetAdminsResponse, GetRolesResponse, Role } from './types'
import { roleLabelsMap } from './constants'

const columns = [
  { title: 'ID', field: 'id' },
  { title: 'Email', field: 'email' },
  {
    title: 'Roles',
    render: (admin: Admin) =>
      admin.roles.length
        ? admin.roles.map(role => (
            <Box key={role.id} mr={1} display="inline-block">
              <Chip
                size="small"
                label={roleLabelsMap[role.name].label}
                color={roleLabelsMap[role.name].chipColor}
              />
            </Box>
          ))
        : STRINGS.TABLE_COLUMN_NOT_DEFINED,
    sorting: false,
  },
]

const getAdminsResponseGetter = (responseData: GetAdminsResponse) => responseData?.admins ?? []
const getRolesResponseGetter = (responseData: GetRolesResponse) => responseData?.roles ?? []

const Admins: React.FC = () => {
  const { admin: currentAdmin } = useContext(UserContext)
  const isSuperAdmin = hasRole(currentAdmin, [RoleEnum.SuperAdmin])
  const isAdmin = hasRole(currentAdmin, [RoleEnum.Admin])

  const [{ data: admins, isLoading: isGetAdminsLoading }, fetchAdmins] = useApi<
    GetAdminsResponse,
    Admin[]
  >(getAdmins, getAdminsResponseGetter, {
    baseData: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })

  const [{ data: roles, isLoading: isGetRolesLoading }] = useApi<GetRolesResponse, Role[]>(
    getRoles,
    getRolesResponseGetter,
    {
      baseData: [],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
    },
  )

  const [deleteAdminApi, isDeleteAdminLoading] = useApiCall<number, void>(deleteAdmin)
  const [isConfirmDeletionDialogOpen, setIsConfirmDeletionionDialogOpen] = useState(false)
  const [isNewAdminDialogOpen, setIsNewAdminDialogOpen] = useState(false)
  const [adminToDelete, setAdminToDelete] = useState<Admin | null>(null)
  const [isUpdateAdminDialogOpen, setIsUpdateAdminDialogOpen] = useState(false)
  const [adminToUpdate, setAdminToUpdate] = useState<Admin | null>(null)

  const onCreateAdminDone = () => {
    fetchAdmins()
    setIsNewAdminDialogOpen(false)
  }

  const handleConfirmationDialogClose = () => {
    setIsConfirmDeletionionDialogOpen(false)
    setAdminToDelete(null)
  }

  const handleDeleteAdmin = async () => {
    if (!adminToDelete) return
    const { id } = adminToDelete
    try {
      await deleteAdminApi(id)
      fetchAdmins()
      handleConfirmationDialogClose()
    } catch (err) {
      snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
    }
  }

  const handleUpdateAdminDialogClose = () => {
    setIsUpdateAdminDialogOpen(false)
    setAdminToUpdate(null)
  }

  const onUpdateAdminDone = () => {
    fetchAdmins()
    handleUpdateAdminDialogClose()
  }

  const hasRestrictedRoles = (admin: Admin): boolean => {
    return admin.roles.some(role => role.name === 'super_admin' || role.name === 'admin')
  }

  const actions = [
    {
      icon: 'add',
      tooltip: 'Nuevo admin',
      isFreeAction: true,
      onClick: () => setIsNewAdminDialogOpen(true),
      hidden: !isSuperAdmin && !isAdmin,
    },
    (row: Admin) => ({
      icon: () => <EditIcon />,
      tooltip: `Editar admin`,
      onClick: (_: React.SyntheticEvent, rowData: Admin | Admin[]) => {
        setAdminToUpdate(rowData as Admin)
        setIsUpdateAdminDialogOpen(true)
      },
      hidden: (() => {
        if (isAdmin && !isSuperAdmin) {
          return hasRestrictedRoles(row)
        }
        return false
      })(),
    }),
    () => ({
      icon: 'delete',
      tooltip: `Eliminar admin`,
      onClick: (_: React.SyntheticEvent, rowData: Admin | Admin[]) => {
        setIsConfirmDeletionionDialogOpen(true)
        setAdminToDelete(rowData as Admin)
      },
      hidden: !isSuperAdmin, // Solo SuperAdmin puede eliminar
    }),
  ]

  return (
    <>
      <DefaultTable<Admin>
        title="Admins"
        columns={columns}
        data={admins}
        actions={actions}
        isLoading={isGetAdminsLoading || isGetRolesLoading}
      />
      {isNewAdminDialogOpen && (
        <NewAdminDialog
          handleClose={() => setIsNewAdminDialogOpen(false)}
          onDone={onCreateAdminDone}
          roles={roles}
          currentAdmin={currentAdmin}
        />
      )}
      {isConfirmDeletionDialogOpen && adminToDelete && (
        <Dialog
          isOpen
          showActions
          onCancel={handleConfirmationDialogClose}
          title={`¿Está seguro que desea eliminar a ${adminToDelete.email}?`}
          isLoading={isDeleteAdminLoading}
          onAccept={handleDeleteAdmin}
          okButtonText="Eliminar"
          okButtonProps={{ disabled: isDeleteAdminLoading }}
        />
      )}
      {isUpdateAdminDialogOpen && adminToUpdate && (
        <UpdateAdminDialog
          admin={adminToUpdate}
          handleClose={handleUpdateAdminDialogClose}
          onDone={onUpdateAdminDone}
          roles={roles}
          currentAdmin={currentAdmin}
        />
      )}
    </>
  )
}

export { Admins }
